import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolvePhoto } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion, Reorder } from "framer-motion";
import {
  ArrowLeft, Sparkles, Upload, X, Loader2, Save, GripVertical,
  ShieldCheck, ShieldAlert, CreditCard, LogOut, Trash2, ChevronRight, Mail,
} from "lucide-react";
import ProfileStrength from "@/components/ProfileStrength";

const GENDER_OPTS = [
  { v: "woman", label: "Woman" },
  { v: "man", label: "Man" },
  { v: "nonbinary", label: "Nonbinary" },
];
const INTEREST_OPTS = [
  "Books", "Cinema", "Hiking", "Cooking", "Vinyl", "Travel", "Coffee",
  "Photography", "Yoga", "Cycling", "Art", "Music", "Dance", "Gardening",
  "Writing", "Wine", "Gaming", "Theatre", "Ceramics", "Architecture",
];

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [location, setLocation] = useState("");
  const [gender, setGender] = useState("");
  const [interestedIn, setInterestedIn] = useState([]);
  const [bio, setBio] = useState("");
  const [interests, setInterests] = useState([]);
  const [ageMin, setAgeMin] = useState(21);
  const [ageMax, setAgeMax] = useState(45);
  const [maxDistance, setMaxDistance] = useState("");
  const [notifyMatch, setNotifyMatch] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [polishing, setPolishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (user && !hydrated) {
      setName(user.name || "");
      setAge(user.age || "");
      setLocation(user.location || "");
      setGender(user.gender || "");
      setInterestedIn(user.interested_in || []);
      setBio(user.bio || "");
      setInterests(user.interests || []);
      setAgeMin(user.age_min ?? 21);
      setAgeMax(user.age_max ?? 45);
      setMaxDistance(user.max_distance_km ?? "");
      setNotifyMatch(user.notify_email_on_match ?? true);
      setPhotos(user.photos || []);
      setHydrated(true);
    }
  }, [user, hydrated]);

  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const polishBio = async () => {
    if (!bio.trim()) { toast.error("Write a draft first"); return; }
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/bio-polish", { bio });
      setBio(data.polished_bio);
      toast.success("Bio refined by Claude");
    } catch {
      toast.error("AI editor is resting");
    } finally {
      setPolishing(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/me/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((p) => [...p, data.photo_url]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (url) => {
    setPhotos((p) => p.filter((x) => x !== url));
    try {
      await api.post("/me/photos/remove", { photo_url: url });
    } catch {
      toast.error("Couldn't remove photo");
    }
  };

  const save = async () => {
    if (ageMin > ageMax) { toast.error("Age min can't exceed max"); return; }
    setSaving(true);
    try {
      await api.put("/me", {
        name, age: age ? Number(age) : null, location, gender,
        interested_in: interestedIn, bio, interests,
        age_min: Number(ageMin), age_max: Number(ageMax),
        max_distance_km: maxDistance === "" ? null : Number(maxDistance),
        notify_email_on_match: notifyMatch,
        photos,
      });
      await refresh();
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete("/me");
      toast.success("Account deleted. Until next time.");
      logout();
      navigate("/");
    } catch {
      toast.error("Couldn't delete account");
    }
  };

  const billing = user?.billing || {};
  const qplus = billing.quietplus;

  return (
    <div className="min-h-screen grain-bg" data-testid="settings-page">
      <header className="px-5 sm:px-10 py-5 flex items-center justify-between border-b border-[#E5DFD3] bg-[#FAF7F2]/80 backdrop-blur sticky top-0 z-30">
        <button data-testid="settings-back" onClick={() => navigate("/app")}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1A1818]">
          <ArrowLeft size={16} /> Back
        </button>
        <span className="font-serif italic text-2xl text-[#913831]">rendezvous</span>
        <button data-testid="settings-save" onClick={save} disabled={saving || !hydrated}
          className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white rounded-full px-5 py-2 text-sm font-medium">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 pb-24 space-y-12">
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.25em] text-[#913831]">Profile</div>
          <h1 className="font-serif text-5xl tracking-tighter text-[#1A1818] mt-2 leading-tight">Settings.</h1>
        </motion.section>

        <ProfileStrength user={user} />

        {/* Photos */}
        <section data-testid="settings-photos">
          <h2 className="font-serif text-2xl text-[#1A1818]">Photos</h2>
          <p className="text-sm text-[#737373] mt-1">Drag to reorder. First photo becomes your main.</p>
          <Reorder.Group axis="y" values={photos} onReorder={setPhotos}
            className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4" as="div" data-testid="settings-photos-grid">
            {photos.map((p, i) => (
              <Reorder.Item key={p} value={p} as="div"
                className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#F0EBE1] border border-[#E5DFD3] cursor-grab active:cursor-grabbing"
                data-testid={`settings-photo-${i}`}
                whileDrag={{ scale: 1.04, zIndex: 5, boxShadow: "0 20px 40px -10px rgba(0,0,0,0.25)" }}
              >
                <img src={resolvePhoto(p)} alt="" className="w-full h-full object-cover pointer-events-none" />
                {i === 0 && (
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] bg-[#913831] text-white px-2 py-0.5 rounded-full">
                    Main
                  </span>
                )}
                <div className="absolute bottom-2 left-2 w-7 h-7 bg-white/85 backdrop-blur rounded-full flex items-center justify-center pointer-events-none">
                  <GripVertical size={14} className="text-[#737373]" />
                </div>
                <button data-testid={`settings-remove-photo-${i}`} type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => removePhoto(p)}
                  className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white">
                  <X size={14} />
                </button>
              </Reorder.Item>
            ))}
            {photos.length < 6 && (
              <label className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#E5DFD3] flex flex-col items-center justify-center cursor-pointer hover:border-[#913831] hover:bg-[#F0EBE1]/40 transition-colors text-[#737373]"
                     data-testid="settings-upload-zone">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto}
                  data-testid="settings-photo-input" />
                {uploading
                  ? <Loader2 size={20} className="animate-spin" />
                  : <><Upload size={22} strokeWidth={1.5} /><span className="text-xs uppercase tracking-[0.2em] mt-2">Add photo</span></>}
              </label>
            )}
          </Reorder.Group>
        </section>

        {/* About me */}
        <section data-testid="settings-about">
          <h2 className="font-serif text-2xl text-[#1A1818]">About you</h2>
          <div className="mt-5 space-y-6">
            <Field label="Name">
              <input data-testid="set-name" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Age">
                <input data-testid="set-age" type="number" min="18" max="99" value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
              </Field>
              <Field label="City">
                <input data-testid="set-location" value={location} onChange={(e) => setLocation(e.target.value)}
                  className="w-full bg-transparent border-b border-[#E5DFD3] focus:border-[#913831] focus:outline-none py-2 text-2xl font-serif" />
              </Field>
            </div>
            <Field label="I am">
              <ChipRow opts={GENDER_OPTS} value={gender} singleValue testid="set-gender"
                onPick={(v) => setGender(v)} />
            </Field>
            <Field label="Interested in">
              <ChipRow opts={GENDER_OPTS} values={interestedIn} testid="set-interested"
                onPick={(v) => setInterestedIn(toggle(interestedIn, v))} />
            </Field>
            <Field label={`Show me ages ${ageMin}–${ageMax}`}>
              <div className="flex gap-3 items-center mt-2">
                <input data-testid="set-age-min" type="number" min="18" max="99" value={ageMin}
                  onChange={(e) => setAgeMin(Number(e.target.value))}
                  className="w-20 bg-white border border-[#E5DFD3] rounded-full px-3 py-1.5 text-sm" />
                <span className="text-[#737373]">to</span>
                <input data-testid="set-age-max" type="number" min="18" max="99" value={ageMax}
                  onChange={(e) => setAgeMax(Number(e.target.value))}
                  className="w-20 bg-white border border-[#E5DFD3] rounded-full px-3 py-1.5 text-sm" />
              </div>
            </Field>
            <Field label={maxDistance === "" || maxDistance === null
              ? "Maximum distance — anywhere in the world"
              : `Maximum distance — ${maxDistance} km`}>
              <div className="mt-2 flex items-center gap-3" data-testid="set-distance-row">
                <input data-testid="set-distance" type="range" min="5" max="20000" step="5"
                  value={maxDistance === "" || maxDistance === null ? 20000 : maxDistance}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setMaxDistance(v >= 20000 ? "" : v);
                  }}
                  className="flex-1 accent-[#913831]" />
                <button type="button" data-testid="distance-anywhere"
                  onClick={() => setMaxDistance("")}
                  className="text-xs text-[#737373] hover:text-[#1A1818] whitespace-nowrap">
                  Anywhere
                </button>
              </div>
              {!user?.location && (
                <p className="text-xs text-[#737373] mt-2 italic">Add a city above for distance-based filtering.</p>
              )}
            </Field>
            <Field label="Your bio">
              <textarea data-testid="set-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={5}
                className="w-full bg-white border border-[#E5DFD3] rounded-2xl p-4 text-sm focus:outline-none focus:border-[#913831] resize-none" />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-[#737373]">{bio.length}/600</span>
                <button data-testid="set-polish" type="button" onClick={polishBio} disabled={polishing}
                  className="inline-flex items-center gap-2 bg-[#F3E8DD] hover:bg-[#EADBC8] text-[#1A1818] rounded-full px-4 py-1.5 text-xs font-medium border border-[#D4A373]/40 disabled:opacity-50">
                  {polishing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} className="text-[#913831]" />}
                  Refine with Claude
                </button>
              </div>
            </Field>
            <Field label="Interests">
              <div className="flex flex-wrap gap-2 mt-2">
                {INTEREST_OPTS.map((t) => (
                  <button key={t} data-testid={`set-interest-${t}`} type="button"
                    onClick={() => setInterests(toggle(interests, t))}
                    className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                      interests.includes(t)
                        ? "bg-[#1A1818] text-white border-[#1A1818]"
                        : "bg-transparent text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
                    }`}>{t}</button>
                ))}
              </div>
            </Field>
          </div>
        </section>

        {/* Account links */}
        <section data-testid="settings-account">
          <h2 className="font-serif text-2xl text-[#1A1818]">Account</h2>
          <div className="mt-4 bg-white border border-[#E5DFD3] rounded-3xl overflow-hidden">
            <div className="flex items-center gap-3 p-4 border-b border-[#E5DFD3]" data-testid="row-email-notif">
              <span className="w-9 h-9 rounded-full bg-[#F3E8DD] border border-[#D4A373]/30 flex items-center justify-center text-[#913831]">
                <Mail size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[#1A1818] font-medium">Email me on a new match</div>
                <div className="text-xs text-[#737373]">A short note when someone says yes back.</div>
              </div>
              <button data-testid="toggle-notify-match" type="button"
                onClick={() => setNotifyMatch((v) => !v)}
                className={`w-11 h-6 rounded-full p-0.5 transition-colors ${notifyMatch ? "bg-[#913831]" : "bg-[#E5DFD3]"}`}>
                <span className={`block w-5 h-5 rounded-full bg-white transform transition-transform ${notifyMatch ? "translate-x-5" : ""}`} />
              </button>
            </div>
            <Row to="/billing" testid="row-billing" icon={<CreditCard size={16} />}
              label={qplus ? "Quiet+ active" : "Upgrade to Quiet+"}
              note={qplus
                ? `Renews / expires ${new Date(billing.subscription_expires_at).toLocaleDateString()}`
                : "Unlimited likes, see who liked you, AI Coach"} />
            <Row to="/verify" testid="row-verify" icon={user?.verified
                ? <ShieldCheck size={16} className="text-[#913831]" />
                : <ShieldAlert size={16} className="text-[#913831]" />}
              label={user?.verified ? "Identity verified" : "Verify your identity"}
              note={user?.verified ? "Your profile has the verified badge" : "Earn 3× more matches"} />
            <button data-testid="row-logout" onClick={() => { logout(); navigate("/"); }}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#F0EBE1] text-left border-t border-[#E5DFD3]">
              <LogOut size={16} className="text-[#737373]" />
              <span className="flex-1 text-[#1A1818]">Sign out</span>
            </button>
          </div>
        </section>

        {/* Danger zone */}
        <section data-testid="settings-danger">
          <h2 className="font-serif text-2xl text-[#D9534F]">Danger zone</h2>
          <div className="mt-4 bg-[#FFF4F1] border border-[#D9534F]/30 rounded-3xl p-5">
            <div className="flex items-start gap-4">
              <Trash2 size={18} className="text-[#D9534F] mt-1 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-serif text-lg text-[#1A1818]">Delete account</div>
                <p className="text-sm text-[#737373] mt-1">
                  Permanently removes your profile, photos, matches, messages, and history. Cannot be undone.
                </p>
                {!confirmDelete ? (
                  <button data-testid="delete-init" onClick={() => setConfirmDelete(true)}
                    className="mt-3 border border-[#D9534F] text-[#D9534F] hover:bg-[#D9534F] hover:text-white rounded-full px-5 py-2 text-sm font-medium">
                    Delete my account
                  </button>
                ) : (
                  <div className="mt-3 flex gap-3">
                    <button data-testid="delete-confirm" onClick={doDelete}
                      className="bg-[#D9534F] hover:bg-[#B23B37] text-white rounded-full px-5 py-2 text-sm font-medium">
                      Yes, delete forever
                    </button>
                    <button data-testid="delete-cancel" onClick={() => setConfirmDelete(false)}
                      className="border border-[#E5DFD3] hover:bg-[#F0EBE1] rounded-full px-5 py-2 text-sm">
                      Keep my account
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[#737373]">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ChipRow({ opts, value, values, singleValue, onPick, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map((o) => {
        const active = singleValue ? value === o.v : (values || []).includes(o.v);
        return (
          <button key={o.v} data-testid={`${testid}-${o.v}`} type="button"
            onClick={() => onPick(o.v)}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              active ? "bg-[#913831] text-white border-[#913831]"
                : "bg-transparent text-[#1A1818] border-[#E5DFD3] hover:bg-[#F0EBE1]"
            }`}>{o.label}</button>
        );
      })}
    </div>
  );
}

function Row({ to, label, note, icon, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="w-full flex items-center gap-3 p-4 hover:bg-[#F0EBE1] transition-colors group">
      <span className="w-9 h-9 rounded-full bg-[#F3E8DD] border border-[#D4A373]/30 flex items-center justify-center text-[#913831]">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[#1A1818] font-medium">{label}</div>
        <div className="text-xs text-[#737373] truncate">{note}</div>
      </div>
      <ChevronRight size={16} className="text-[#737373] group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}
